import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router-dom";
import { Title, useCreatePath } from "react-admin";
import type {
  DashboardDocumentationStats,
  DashboardHeroState,
  DashboardOnboardingStepKind,
  DashboardRecentJobReport,
  DashboardSnapshot,
  DashboardTodayJob,
  JobState,
} from "@/domain/model/Dashboard";
import { useDashboardViewModel } from "./DashboardViewModel";
import {
  PageContent,
  PageHeader,
  SectionCard,
  SummaryStatCard,
  layout,
} from "./../layout";
import type { ReactNode } from "react";
import { InviteUserDialog } from "../components/InviteUserDialog";
import { useDI } from "@/infrastructure/di/useDI";

export function Dashboard() {
  const { getDashboardSnapshot, inviteAccountUser } = useDI();
  const { state, intents } = useDashboardViewModel({getDashboardSnapshot, inviteAccountUser});
  const { loading, error, snapshot } = state;

  if (loading && !snapshot) {
    return <DashboardSkeleton />;
  }

  if (error && !snapshot) {
    return (
      <PageContent width="wide">
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={intents.reload}>
              Erneut laden
            </Button>
          }
        >
          {error}
        </Alert>
      </PageContent>
    );
  }

  if (!snapshot) {
    return null;
  }

  return (
    <>
      <Title title="Dashboard" />
      <PageContent width="wide">
        <Stack spacing={layout.section.gap}>
          <PageHeader
            title="Dashboard"
            subtitle="Tagesüberblick für Aufträge, Berichte und Einrichtung."
            actions={
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={intents.reload}
                disabled={loading}
              >
                Aktualisieren
              </Button>
            }
          />

         {state.actionError ? (
           <Alert severity="error" onClose={intents.clearMessages}>
             {state.actionError}
           </Alert>
         ) : null}

          {state.successMessage ? (
            <Alert severity="success" onClose={intents.clearMessages}>
              {state.successMessage}
            </Alert>
          ) : null}

          <DashboardHeroCard snapshot={snapshot} loading={loading} onInviteAppUser={intents.openInviteDialog} />

          <DashboardSummaryRow snapshot={snapshot} />

          <Grid container spacing={layout.section.gap}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={layout.section.gap}>
                <TodayJobsCard snapshot={snapshot} />
                <RecentReportsCard snapshot={snapshot} />
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={layout.section.gap}>
                <QuickActionsCard />

                {snapshot.hero.state !== "OPERATIONS" ? (
                  <SetupChecklistCard snapshot={snapshot} onInviteAppUser={intents.openInviteDialog} />
                ) : null}

                <WeekStatsCard snapshot={snapshot} />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </PageContent>

      <InviteUserDialog
        open={state.inviteDialog.open}
        email={state.inviteDialog.email}
        role={state.inviteDialog.role}
        isBusy={state.activeAction === "invite"}
        onClose={intents.closeInviteDialog}
        onEmailChange={intents.setInviteEmail}
        onRoleChange={intents.setInviteRole}
        onSubmit={() => void intents.submitInvite()}
      />
    </>
  );
}

function DashboardHeroCard({
  snapshot,
  loading,
  onInviteAppUser,
}: {
  snapshot: DashboardSnapshot;
  loading: boolean;
  onInviteAppUser: () => void;
}) {
  const createPath = useCreatePath();
  const hero = getHeroContent(snapshot.hero.state, snapshot, createPath);

  const isOnboarding = snapshot.hero.state !== "OPERATIONS";
  const steps = snapshot.hero.onboardingSteps;
  const completedCount = steps.filter((step) => step.completed).length;

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: { xs: 2.5, md: 3 },
        borderRadius: layout.card.radius,
        overflow: "hidden",

        ...(isOnboarding
          ? {
            color: "primary.contrastText",
            bgcolor: "primary.main",
            borderColor: "transparent",
            backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          }
          : {
            bgcolor: "background.paper",
          }),
      })}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2.5}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          {isOnboarding ? (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(255,255,255,0.16)",
                flexShrink: 0,
              }}
            >
              {hero.icon}
            </Box>
          ) : null}

          <Box sx={{ minWidth: 0 }}>
            {hero.eyebrow ? (
              <Chip
                size="small"
                label={hero.eyebrow}
                sx={
                  isOnboarding
                    ? {
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "inherit",
                      fontWeight: 700,
                    }
                    : undefined
                }
              />
            ) : null}

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography
                variant="h5"
                component="h2"
                fontWeight={800}
                sx={{ mt: hero.eyebrow ? 1 : 0 }}
              >
                {hero.title}
              </Typography>

              {loading ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label="lädt…"
                  sx={
                    isOnboarding
                      ? { color: "inherit", borderColor: "currentColor" }
                      : undefined
                  }
                />
              ) : null}
            </Stack>

            <Typography
              sx={{
                mt: 0.75,
                maxWidth: 680,
                color: isOnboarding ? "inherit" : "text.secondary",
                opacity: isOnboarding ? 0.9 : 1,
              }}
            >
              {hero.subtitle}
            </Typography>

            {isOnboarding ? (
              <Typography variant="body2" sx={{ mt: 1.25, opacity: 0.85 }}>
                {completedCount} von {steps.length} Einrichtungsschritten erledigt
              </Typography>
            ) : null}
          </Box>
        </Stack>

        {hero.action.type === "link" ? (
          <Button
            component={RouterLink}
            to={hero.action.to}
            variant="contained"
            startIcon={isOnboarding ? undefined : hero.icon}
            sx={{
              flexShrink: 0,

              ...(isOnboarding
                ? {
                  bgcolor: "background.paper",
                  color: "primary.main",
                  fontWeight: 700,
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "background.paper",
                    boxShadow: 4,
                  },
                }
                : undefined),
            }}
          >
            {hero.action.label}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onInviteAppUser}
            sx={{
              flexShrink: 0,
              bgcolor: "background.paper",
              color: "primary.main",
              fontWeight: 700,
              boxShadow: 2,
              "&:hover": {
                bgcolor: "background.paper",
                boxShadow: 4,
              },
            }}
          >
            {hero.action.label}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

function DashboardSummaryRow({ snapshot }: { snapshot: DashboardSnapshot }) {
  return (
    <Grid container spacing={layout.section.gap}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <SummaryStatCard
          label="Heute geplant"
          value={snapshot.today.plannedCount}
          subtext="Aufträge mit geplanter Ausführung"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <SummaryStatCard
          label="Aktiv"
          value={snapshot.today.activeCount}
          subtext="Aktuell in Arbeit oder Pause"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <SummaryStatCard
          label="Heute abgeschlossen"
          value={snapshot.today.completedCount}
          subtext="Bereits erledigte Aufträge"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <SummaryStatCard
          label="Diese Woche"
          value={formatDuration(snapshot.week.trackedDurationMs)}
          subtext="Dokumentierte Arbeitszeit"
        />
      </Grid>
    </Grid>
  );
}

function TodayJobsCard({ snapshot }: { snapshot: DashboardSnapshot }) {
  return (
    <SectionCard
      title="Heute geplant"
      subtitle={`${snapshot.today.plannedCount} geplant · ${snapshot.today.activeCount} aktiv · ${snapshot.today.completedCount} abgeschlossen`}
    >
      {snapshot.today.jobs.length === 0 ? (
        <EmptyText>Für heute sind keine Aufträge geplant.</EmptyText>
      ) : (
        <Stack spacing={1.5}>
          {snapshot.today.jobs.map((job, index) => (
            <Box key={job.id}>
              {index > 0 ? <Divider sx={{ mb: 1.5 }} /> : null}
              <TodayJobRow job={job} timezone={snapshot.timezone} />
            </Box>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}

function TodayJobRow({
  job,
  timezone,
}: {
  job: DashboardTodayJob;
  timezone: string;
}) {
  const createPath = useCreatePath();

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
    >
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography fontWeight={700}>
            {job.plannedStartAt
              ? formatTime(job.plannedStartAt, timezone)
              : "Ohne Uhrzeit"}
          </Typography>

          <Chip
            size="small"
            label={getJobStateLabel(job.state)}
            color={getJobStateColor(job.state)}
          />
        </Stack>

        <Typography sx={{ mt: 0.5 }} noWrap>
          {job.title}
        </Typography>

        <Typography variant="body2" color="text.secondary" noWrap>
          {formatJobMeta(job)}
        </Typography>
      </Box>

      <Button
        component={RouterLink}
        to={createPath({ resource: "jobs", id: job.id, type: "show" })}
        size="small"
        variant="outlined"
        endIcon={<OpenInNewIcon />}
        sx={{ flexShrink: 0 }}
      >
        Öffnen
      </Button>
    </Stack>
  );
}

function RecentReportsCard({ snapshot }: { snapshot: DashboardSnapshot }) {
  return (
    <SectionCard title="Zuletzt abgeschlossene Aufträge">
      {snapshot.recentReports.length === 0 ? (
        <EmptyText>
          Noch keine abgeschlossenen Auftragsberichte vorhanden.
        </EmptyText>
      ) : (
        <Stack spacing={1.5}>
          {snapshot.recentReports.map((report, index) => (
            <Box key={report.jobId}>
              {index > 0 ? <Divider sx={{ mb: 1.5 }} /> : null}
              <RecentReportRow report={report} timezone={snapshot.timezone} />
            </Box>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}

function RecentReportRow({
  report,
  timezone,
}: {
  report: DashboardRecentJobReport;
  timezone: string;
}) {
  const createPath = useCreatePath();

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography fontWeight={700} noWrap>
          {report.title}
        </Typography>

        <Typography variant="body2" color="text.secondary" noWrap>
          {[
            report.clientName,
            report.constructionSiteLabel,
            formatDateTime(report.completedAt, timezone),
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
          <Chip
            size="small"
            label={`${formatDuration(report.trackedDurationMs)} erfasst`}
          />
          <Chip
            size="small"
            label={formatDocumentation(report.documentation)}
          />
        </Stack>
      </Box>

      <Button
        component={RouterLink}
        to={createPath({ resource: "jobs", id: report.jobId, type: "show" })}
        size="small"
        variant="outlined"
        sx={{ flexShrink: 0 }}
      >
        Bericht ansehen
      </Button>
    </Stack>
  );
}

function QuickActionsCard() {
  const createPath = useCreatePath();

  return (
    <SectionCard title="Schnellaktionen">
      <Stack spacing={1}>
        <Button
          component={RouterLink}
          to={createPath({ resource: "jobs", type: "create" })}
          variant="contained"
          startIcon={<AddIcon />}
          fullWidth
        >
          Auftrag erstellen
        </Button>

        <Button
          component={RouterLink}
          to={createPath({ resource: "persons", type: "create" })}
          variant="outlined"
          startIcon={<PersonAddIcon />}
          fullWidth
        >
          Mitarbeiter hinzufügen
        </Button>

        <Button
          component={RouterLink}
          to={createPath({ resource: "jobs", type: "list" })}
          variant="outlined"
          fullWidth
        >
          Aufträge anzeigen
        </Button>
      </Stack>
    </SectionCard>
  );
}

function SetupChecklistCard({
  snapshot,
  onInviteAppUser
}: {
  snapshot: DashboardSnapshot;
  onInviteAppUser: () => void;
}) {
  const createPath = useCreatePath();

  const steps = snapshot.hero.onboardingSteps;
  const completedCount = steps.filter((step) => step.completed).length;
  const progress = Math.round((completedCount / steps.length) * 100);
  const nextOpenStep = steps.find((step) => !step.completed);

  return (
    <SectionCard title="Einrichtung abschließen">
      <Stack spacing={2}>
        <Box
          sx={(theme) => ({
            p: 2,
            borderRadius: 2,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          })}
        >
          <Typography fontWeight={700}>
            Onboarding
          </Typography>

          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
            Richten Sie Logami in wenigen Schritten vollständig ein.
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,0.3)",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "primary.contrastText",
                },
              }}
            />

            <Typography variant="body2" fontWeight={700}>
              {completedCount}/{steps.length}
            </Typography>
          </Stack>
        </Box>

        <Stack spacing={1.25}>
          {steps.map((step) => {
            const action = getOnboardingStepAction(step.kind, createPath);
            const isNext = step.kind === nextOpenStep?.kind;

            return (
              <Box
                key={step.kind}
                sx={(theme) => ({
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isNext ? "primary.main" : "divider",
                  bgcolor: isNext
                    ? theme.palette.action.hover
                    : "transparent",
                })}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                >
                  <Chip
                    size="small"
                    label={step.completed ? "Erledigt" : isNext ? "Nächster Schritt" : "Offen"}
                    color={step.completed ? "success" : isNext ? "primary" : "default"}
                    variant={step.completed ? "filled" : "outlined"}
                  />

                  <Typography
                    sx={{ flex: 1 }}
                    fontWeight={isNext ? 700 : 400}
                    color={step.completed ? "text.secondary" : "text.primary"}
                  >
                    {getOnboardingStepLabel(step.kind)}
                  </Typography>

                  {!step.completed && action ? (
  action.type === "link" ? (
    <Button
      component={RouterLink}
      to={action.to}
      size="small"
      variant={isNext ? "contained" : "text"}
    >
      {action.label}
    </Button>
  ) : (
    <Button
      size="small"
      variant={isNext ? "contained" : "text"}
      onClick={onInviteAppUser}
    >
      {action.label}
    </Button>
  )
) : null}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Stack>
    </SectionCard>
  );
}

function WeekStatsCard({ snapshot }: { snapshot: DashboardSnapshot }) {
  return (
    <SectionCard
      title="Diese Woche"
      subtitle={`${formatIsoDate(snapshot.week.periodStart)} – ${formatIsoDate(
        addDays(snapshot.week.periodEndExclusive, -1),
      )}`}
    >
      <Stack spacing={1}>
        <StatLine
          label="Abgeschlossene Aufträge"
          value={String(snapshot.week.completedJobs)}
        />
        <StatLine
          label="Dokumentierte Zeit"
          value={formatDuration(snapshot.week.trackedDurationMs)}
        />
        <StatLine
          label="Dokumentation"
          value={formatDocumentation(snapshot.week.documentation)}
        />
      </Stack>
    </SectionCard>
  );
}

function StatLine({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography color="text.secondary">{label}</Typography>
      <Typography fontWeight={700}>{value}</Typography>
    </Stack>
  );
}

function EmptyText({ children }: { children: ReactNode }) {
  return (
    <Typography color="text.secondary" variant="body2">
      {children}
    </Typography>
  );
}

function DashboardSkeleton() {
  return (
    <PageContent width="wide">
      <Stack spacing={layout.section.gap}>
        <Box>
          <Skeleton width={220} height={44} />
          <Skeleton width="50%" height={28} />
        </Box>

        <Skeleton
          variant="rounded"
          height={128}
          sx={{ borderRadius: layout.card.radius }}
        />

        <Grid container spacing={layout.section.gap}>
          {[0, 1, 2, 3].map((item) => (
            <Grid key={item} size={{ xs: 12, sm: 6, md: 3 }}>
              <Skeleton
                variant="rounded"
                height={136}
                sx={{ borderRadius: layout.card.radius }}
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={layout.section.gap}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={layout.section.gap}>
              <Skeleton
                variant="rounded"
                height={260}
                sx={{ borderRadius: layout.card.radius }}
              />
              <Skeleton
                variant="rounded"
                height={220}
                sx={{ borderRadius: layout.card.radius }}
              />
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={layout.section.gap}>
              <Skeleton
                variant="rounded"
                height={180}
                sx={{ borderRadius: layout.card.radius }}
              />
              <Skeleton
                variant="rounded"
                height={180}
                sx={{ borderRadius: layout.card.radius }}
              />
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </PageContent>
  );
}

type DashboardHeroAction =
  | {
    type: "link";
    label: string;
    to: string;
  }
  | {
    type: "inviteAppUser";
    label: string;
  };

type DashboardHeroContent = {
  title: string;
  subtitle: string;
  action: DashboardHeroAction;
  icon: React.ReactNode;
  eyebrow?: string;
};

function getHeroContent(
  state: DashboardHeroState,
  snapshot: DashboardSnapshot,
  createPath: ReturnType<typeof useCreatePath>,
): DashboardHeroContent {
  switch (state) {
    case "ONBOARDING_ENABLE_APP_ACCESS":
      return {
        eyebrow: "Einrichtung",
        title: "App-Zugriff einrichten",
        subtitle: "Laden Sie einen Mitarbeiter ein oder aktivieren Sie App-Zugriff für einen bestehenden Mitarbeiter. Dafür wird ein freier App-Zugang benötigt.",
        action: {
          type: "inviteAppUser",
          label: "App-Nutzer einladen",
        },
        icon: <PersonAddIcon />,
      };

    case "ONBOARDING_FIRST_JOB":
      return {
        eyebrow: "Nächster Schritt",
        title: "Ersten Auftrag erstellen",
        subtitle: "Erstellen Sie den ersten Auftrag und weisen Sie ihn einem App-Nutzer zu.",
        action: {
          type: "link",
          label: "Ersten Auftrag erstellen",
          to: createPath({ resource: "jobs", type: "create" }),
        },
        icon: <AddIcon />,
      };

    case "ONBOARDING_BUY_SEATS":
      return {
        eyebrow: "App-Zugänge",
        title: "Weiterer App-Zugang benötigt",
        subtitle:
          "Für jeden Mitarbeiter mit App-Zugriff wird ein freier App-Zugang benötigt. Buchen Sie einen weiteren Zugang, um jemanden einzuladen.",
        action: {
          type: "link",
          label: "App-Zugänge verwalten",
          to: "/account/billing",
        },
        icon: <PersonAddIcon />,
      };

    case "OPERATIONS":
      return {
        title: "Willkommen zurück",
        subtitle: `${snapshot.hero.todayPlannedCount} geplant · ${snapshot.hero.todayActiveCount} aktiv · ${snapshot.hero.todayCompletedCount} abgeschlossen`,
        action: {
          type: "link",
          label: "Auftrag erstellen",
          to: createPath({ resource: "jobs", type: "create" }),
        },
        icon: <AddIcon />,
      };
  }
}

function getOnboardingStepLabel(kind: DashboardOnboardingStepKind): string {
  switch (kind) {
    case "ENABLE_APP_ACCESS":
      return "App-Zugriff einrichten";

    case "CREATE_FIRST_JOB":
      return "Ersten Auftrag erstellen";

    case "BUY_SEATS":
      return "Weiteren App-Zugang buchen";
  }
}

type OnboardingStepAction =
  | {
      type: "link";
      label: string;
      to: string;
    }
  | {
      type: "inviteAppUser";
      label: string;
    };

function getOnboardingStepAction(
  kind: DashboardOnboardingStepKind,
  createPath: ReturnType<typeof useCreatePath>,
): OnboardingStepAction | null {
  switch (kind) {
    case "ENABLE_APP_ACCESS":
      return {
        type: "inviteAppUser",
        label: "Einladen",
      };

    case "CREATE_FIRST_JOB":
      return {
        type: "link",
        label: "Erstellen",
        to: createPath({ resource: "jobs", type: "create" }),
      };

    case "BUY_SEATS":
      return {
        type: "link",
        label: "Verwalten",
        to: "/account/billing",
      };
  }
}

function formatJobMeta(job: DashboardTodayJob): string {
  return [
    job.clientName,
    formatConstructionSite(job),
    job.assignedPerson?.displayName,
  ]
    .filter(Boolean)
    .join(" · ");
}

function formatConstructionSite(job: DashboardTodayJob): string | null {
  if (job.constructionSite.label) {
    return job.constructionSite.label;
  }

  const address = job.constructionSite.address;
  if (!address) return null;

  const street = [address.street, address.streetNumber].filter(Boolean).join(" ");
  const city = [address.zip, address.city].filter(Boolean).join(" ");

  return [street, city].filter(Boolean).join(", ") || null;
}

function getJobStateLabel(state: JobState): string {
  switch (state) {
    case "OPEN":
      return "Offen";
    case "IN_PROGRESS":
      return "In Arbeit";
    case "ON_BREAK":
      return "Pause";
    case "INTERRUPTED":
      return "Unterbrochen";
    case "CLOSED_FOR_DAY":
      return "Heute geschlossen";
    case "COMPLETED":
      return "Abgeschlossen";
  }
}

function getJobStateColor(
  state: JobState,
): "default" | "info" | "success" | "warning" {
  switch (state) {
    case "OPEN":
      return "default";
    case "IN_PROGRESS":
      return "success";
    case "ON_BREAK":
    case "INTERRUPTED":
      return "warning";
    case "CLOSED_FOR_DAY":
      return "info";
    case "COMPLETED":
      return "success";
  }
}

function formatDocumentation(stats: DashboardDocumentationStats): string {
  const total = stats.photoCount + stats.noteCount + stats.signatureCount;

  if (total === 0) {
    return "Keine Dokumentation";
  }

  const parts = [
    stats.photoCount > 0 ? `${stats.photoCount} Fotos` : null,
    stats.noteCount > 0 ? `${stats.noteCount} Notizen` : null,
    stats.signatureCount > 0 ? `${stats.signatureCount} Unterschriften` : null,
  ].filter(Boolean);

  return parts.join(" · ");
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null || ms <= 0) return "0 min";

  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }

  if (minutes === 0) {
    return `${hours} h`;
  }

  return `${hours} h ${minutes} min`;
}

function formatTime(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timezone,
  }).format(date);
}

function formatDateTime(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  }).format(date);
}

function formatIsoDate(value: string): string {
  const [year, month, day] = value.split("-");
  return `${day}.${month}.${year}`;
}

function addDays(value: string, days: number): string {
  const date = new Date(`${value}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
